/**
 * Which ad or link brought this visitor, carried through to Google Play.
 *
 * An install from the Play Store loses the page it came from, so Play Console
 * would count a Meta ad click as an organic install. Play reads a `referrer`
 * parameter on the store link and reports its utm_* fields under the store
 * listing's acquisition report, which is the only place an install can be tied
 * back to a campaign without an SDK in the app.
 *
 * The tags are kept for the tab's session only: a visitor who lands from an ad
 * and reads three pages before tapping Play is still that ad's install.
 */

const STORAGE_KEY = "vm-attribution";

/** The only fields Play's install referrer reports. */
const UTM_FIELDS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];

/** What a visit with no tags reports, so Play can tell site traffic from store search. */
const SITE_DEFAULT = { utm_source: "varzishmate.com", utm_medium: "website" };

const sessionStore = () => {
  try {
    return window.sessionStorage;
  } catch {
    return null; // Storage blocked (private mode, sandboxed frame): run untagged.
  }
};

/** The utm_* fields in a query string, or null when it carries none. */
export function readAttribution(search) {
  const params = new URLSearchParams(search);
  const found = {};
  for (const field of UTM_FIELDS) {
    const value = params.get(field)?.trim();
    if (value) found[field] = value.slice(0, 100);
  }
  return Object.keys(found).length ? found : null;
}

/**
 * Save the landing page's tags. A later tagged landing in the same session
 * replaces them (the newest ad gets the credit); an untagged page leaves
 * them alone.
 */
export function rememberAttribution(search = window.location.search, storage = sessionStore()) {
  const found = readAttribution(search);
  if (!found || !storage) return;
  try {
    storage.setItem(STORAGE_KEY, JSON.stringify(found));
  } catch {
    /* quota or privacy mode: the link goes out untagged */
  }
}

export function storedAttribution(storage = sessionStore()) {
  try {
    const saved = JSON.parse(storage?.getItem(STORAGE_KEY) || "null");
    return saved && typeof saved === "object" ? saved : null;
  } catch {
    return null;
  }
}

/**
 * The store link with Play's `referrer` added. A link that already carries
 * one is left as it is.
 */
export function withPlayReferrer(url, attribution = storedAttribution()) {
  if (/[?&]referrer=/.test(url)) return url;
  const tags = attribution || SITE_DEFAULT;
  const referrer = new URLSearchParams();
  for (const field of UTM_FIELDS) if (tags[field]) referrer.set(field, tags[field]);
  const joiner = url.includes("?") ? "&" : "?";
  return `${url}${joiner}referrer=${encodeURIComponent(referrer.toString())}`;
}

/**
 * The App Store link with Apple's campaign tags added, so App Store Connect's
 * Analytics, Sources report counts the download under the ad that brought the
 * visitor. Apple has no `referrer`: `ct` is the only label that survives, and
 * it takes up to 40 letters, digits, hyphens and underscores. `pt` names the
 * developer account and is the same on every link; without it Apple drops `ct`.
 */
export function withAppStoreCampaign(url, providerToken, attribution = storedAttribution()) {
  if (!providerToken || /[?&]ct=/.test(url)) return url;
  const tags = attribution || SITE_DEFAULT;
  const parts = [tags.utm_source, tags.utm_campaign].filter(Boolean);
  const campaign = parts.join("_").replace(/[^A-Za-z0-9_-]/g, "-").slice(0, 40);
  const joiner = url.includes("?") ? "&" : "?";
  return `${url}${joiner}pt=${encodeURIComponent(providerToken)}&ct=${campaign}`;
}

/**
 * The web-app link with the ad's utm_* tags, so the console (another domain)
 * can tell which ad a new signup came from. A link that already has a query
 * is left alone; an untagged visit sends nothing.
 */
export function withUtmTags(url, attribution = storedAttribution()) {
  if (!attribution || url.includes("?")) return url;
  const query = new URLSearchParams();
  for (const field of UTM_FIELDS) if (attribution[field]) query.set(field, attribution[field]);
  const text = query.toString();
  return text ? `${url}?${text}` : url;
}
